// Drugs › Measured ΔFC, the matrix: the condition's 432 × 432 signed changes
// in a sheet over the scene, one cell per pair of parcels on the export's own order (LH cortex, RH cortex, subcortex), the
// matrix scale ±h_m with the change map, a named grey where no edge was measured (NaN, no one behind it, the diagonal), hover
// reading the pair, Δz and the people, a click picking the row's parcel. The matrix is the export's largest file and reads
// once (features/dfc.ts `matrix()`), so the sheet says "Reading the matrix…" the first time. Along both axes, the seven
// Yeo networks of each hemisphere as coloured bands (the atlas's own colours; dfc.ts `networkBands`), faint dividers
// between them on the canvas, and the networks named in the readout and a key, so a block of change reads as a network.

import { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { IconButton } from '../ui/primitives.tsx';
import { useLoaded } from '../ui/hooks.ts';
import { changeColour } from './changeScale.ts';
import { bandLabel, conditionLabel, dzText, networkBands, networkOf, NETWORKS, parcelName, peopleText, useDfc, type Dfc, type NetworkBand } from './dfc.ts';

/** Where no edge was measured: a grey the change map never reaches, named in the legend. */
export const NO_EDGE: [number, number, number] = [0x6b, 0x70, 0x78];

/** The export's node order as groups with a divider and a mark each: LH cortex, RH cortex, the subcortex (Sub). */
function groupsOf(data: Dfc): { label: string; from: number; to: number }[] {
  const out: { label: string; from: number; to: number }[] = [];
  for (const n of data.nodes) {
    // The subcortex group is 32 cells wide, so its mark is short; the legend and the readouts say it in full.
    const label = n.component === 'cortex' ? (n.name.includes('_LH_') ? 'LH' : 'RH') : 'Sub';
    const last = out[out.length - 1];
    if (last && last.label === label) last.to = n.index;
    else out.push({ label, from: n.index, to: n.index });
  }
  return out;
}

/** The matrix as pixels, one per cell, in the export's order. */
function pixelsOf(data: Dfc, c: number, dz: Float64Array, people: Uint16Array): ImageData {
  const n = data.n;
  const h = data.limits.matrix;
  const img = new ImageData(n, n);
  const d = img.data;
  const base = c * n * n;
  for (let i = 0; i < n; i++)
    for (let j = 0; j < n; j++) {
      const k = base + i * n + j;
      const v = dz[k]!;
      const p = people[k]!;
      const rgb = i === j || p === 0 || !Number.isFinite(v) ? NO_EDGE : changeColour((v + h) / (2 * h));
      const o = (i * n + j) * 4;
      d[o] = rgb[0];
      d[o + 1] = rgb[1];
      d[o + 2] = rgb[2];
      d[o + 3] = 255;
    }
  return img;
}

export function DfcMatrix({ data, onClose }: { data: Dfc; onClose: () => void }) {
  const id = useDfc((s) => s.condition);
  const parcel = useDfc((s) => s.parcel);
  const setParcel = useDfc((s) => s.setParcel);
  const c = data.condition(id);
  const matrix = useLoaded(() => data.matrix(), [data]);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [hover, setHover] = useState<{ i: number; j: number } | null>(null);
  const groups = useMemo(() => groupsOf(data), [data]);
  const bands = useMemo(() => networkBands(data.nodes), [data]);
  const [band, setBand] = useState<NetworkBand | null>(null);
  const sheet = useRef<HTMLElement>(null);
  // Esc closes the sheet before the app's shortcuts see the key (a capture listener on the window runs first and stops it).
  useEffect(() => {
    sheet.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  const h = data.limits.matrix;
  const n = data.n;
  // The square: as much of the sheet as fits, a whole number of device pixels per cell where it can be.
  const SIZE = 648;
  useEffect(() => {
    const el = canvas.current;
    if (!el || !matrix.value) return;
    const dpr = window.devicePixelRatio || 1;
    const px = Math.round(SIZE * dpr);
    if (el.width !== px) el.width = el.height = px;
    const g = el.getContext('2d');
    if (!g) return;
    const off = new OffscreenCanvas(n, n);
    const og = off.getContext('2d');
    if (!og) return;
    og.putImageData(pixelsOf(data, c.index, matrix.value.dz, matrix.value.people), 0, 0);
    g.imageSmoothingEnabled = false;
    g.clearRect(0, 0, px, px);
    g.drawImage(off, 0, 0, px, px);
    // Faint dividers between the network bands, stronger ones between the groups, and the picked parcel's row and column.
    // A line is crisp when its centre sits half a pixel in for an odd width and on a pixel edge for an even one: at DPR 2 the
    // 2 px divider on a half pixel straddled three device pixels and lightened a third more of the cells it crossed (ba-engine,
    // the e2e matrix check at 2×, 10-03).
    const cell = px / n;
    const width = Math.max(1, Math.round(dpr));
    const crisp = width % 2 ? 0.5 : 0;
    g.lineWidth = width;
    g.strokeStyle = 'rgba(255,255,255,0.22)';
    for (const b of bands.slice(1)) {
      if (groups.some((grp) => grp.from === b.from)) continue;
      const at = Math.round(b.from * cell) + crisp;
      g.beginPath();
      g.moveTo(at, 0);
      g.lineTo(at, px);
      g.moveTo(0, at);
      g.lineTo(px, at);
      g.stroke();
    }
    g.strokeStyle = 'rgba(255,255,255,0.55)';
    for (const grp of groups.slice(1)) {
      const at = Math.round(grp.from * cell) + crisp;
      g.beginPath();
      g.moveTo(at, 0);
      g.lineTo(at, px);
      g.moveTo(0, at);
      g.lineTo(px, at);
      g.stroke();
    }
    if (parcel !== null) {
      g.strokeStyle = 'rgba(255,255,255,0.9)';
      g.lineWidth = width;
      g.strokeRect(crisp, Math.round(parcel * cell) + crisp, px - 2 * crisp, Math.max(1, Math.round(cell)) - 2 * crisp);
      g.strokeRect(Math.round(parcel * cell) + crisp, crisp, Math.max(1, Math.round(cell)) - 2 * crisp, px - 2 * crisp);
    }
  }, [data, c.index, matrix.value, groups, bands, parcel, n]);
  const cellAt = (e: { clientX: number; clientY: number; currentTarget: HTMLCanvasElement }) => {
    const r = e.currentTarget.getBoundingClientRect();
    const j = Math.floor(((e.clientX - r.left) / r.width) * n);
    const i = Math.floor(((e.clientY - r.top) / r.height) * n);
    return i >= 0 && i < n && j >= 0 && j < n ? { i, j } : null;
  };
  // The pair under the pointer, from the matrix already read: NaN or no one behind it means no edge.
  const edgeOf = (i: number, j: number) => {
    if (!matrix.value) return null;
    const k = c.index * n * n + i * n + j;
    const v = matrix.value.dz[k]!;
    const p = matrix.value.people[k]!;
    return Number.isFinite(v) && p > 0 ? { dz: v, people: p } : null;
  };
  const edge = hover ? edgeOf(hover.i, hover.j) : null;
  const netName = (i: number) => networkOf(data.nodes[i]!)?.name ?? 'Subcortex';
  // Idle, the readout line carries the networks' key (the atlas's own tokens, as the parcel names read; the full names on hover and
  // in the pair's readout) instead of a line of its own: the sheet is as tall as the window allows.
  const readout = band
    ? `${bandLabel(band)} · rows ${band.from + 1}–${band.to + 1}`
    : !hover
      ? null
      : hover.i === hover.j
        ? `${parcelName(data.nodes[hover.i]!)} · ${netName(hover.i)} · the diagonal, no self edge`
        : edge
          ? `${parcelName(data.nodes[hover.i]!)} ↔ ${parcelName(data.nodes[hover.j]!)} · ${netName(hover.i)} ↔ ${netName(hover.j)} · ${dzText(edge.dz)} Δz · ${peopleText(edge.people)}`
          : `${parcelName(data.nodes[hover.i]!)} ↔ ${parcelName(data.nodes[hover.j]!)} · ${netName(hover.i)} ↔ ${netName(hover.j)} · no edge measured`;
  const bandStyle = (b: NetworkBand) => ({ flex: b.to - b.from + 1, background: b.network ? `rgb(${b.network.rgb.join(',')})` : 'rgba(255,255,255,0.28)' });
  const bandStrip = (where: 'top' | 'left') => (
    <div className={`dfc-matrix-bands ${where}`} aria-label="Networks along the axis" role="img">
      {bands.map((b) => (
        <span key={`${b.hemi}-${b.network?.id ?? 'sub'}`} className={`dfc-band ${b.hemi === 'Sub' ? 'sub' : ''}`} style={bandStyle(b)} title={bandLabel(b)} onPointerEnter={() => setBand(b)} onPointerLeave={() => setBand(null)} />
      ))}
    </div>
  );
  return (
    <div className="sheet-overlay" onClick={onClose} role="presentation">
      <section ref={sheet} tabIndex={-1} className="sheet dfc-matrix" role="dialog" aria-modal="true" aria-label="Connectivity matrix" data-label-exclude="" onClick={(e) => e.stopPropagation()}>
        <header className="sheet-head">
          <h2>{`Measured ΔFC · ${conditionLabel(c)}`}</h2>
          <IconButton icon={X} label="Close" onClick={onClose} />
        </header>
        <div className="dfc-matrix-body">
          <div className="dfc-matrix-axis top ba-type-caption muted">
            {groups.map((g) => (
              <span key={g.label} style={{ flex: g.to - g.from + 1 }}>
                {g.label}
              </span>
            ))}
          </div>
          {bandStrip('top')}
          <div className="dfc-matrix-row">
            <div className="dfc-matrix-axis left ba-type-caption muted">
              {groups.map((g) => (
                <span key={g.label} style={{ flex: g.to - g.from + 1 }}>
                  {g.label}
                </span>
              ))}
            </div>
            {bandStrip('left')}
            <canvas
              ref={canvas}
              className="dfc-matrix-canvas"
              style={{ width: SIZE, height: SIZE }}
              aria-label={`${n} by ${n} parcels: the change in connectivity of each pair, ±${h.toFixed(2)} Δz; grey where no edge was measured`}
              onPointerMove={(e) => setHover(cellAt(e))}
              onPointerLeave={() => setHover(null)}
              onClick={(e) => {
                const at = cellAt(e);
                if (at) setParcel(at.i);
              }}
            />
          </div>
          {!matrix.done ? <p className="dfc-note ba-type-small muted">Reading the matrix ({(data.conn.arrays.delta_fc.byte_length ?? 0) / 1e6 > 0 ? `${Math.round((data.conn.arrays.delta_fc.byte_length ?? 0) / 1e6)} MB` : 'the export’s largest file'})…</p> : null}
          {matrix.error ? <p className="dfc-note ba-type-small muted">{matrix.error}</p> : null}
          <p className="dfc-matrix-readout ba-type-small num">
            {readout ?? (
              <span className="dfc-networks">
                <span className="dfc-networks-lead">Hover a cell for its pair · the bands:</span>
                {NETWORKS.map((net) => (
                  <span key={net.id} className="dfc-net" title={net.name}>
                    <span className="dfc-swatch" style={{ background: `rgb(${net.rgb.join(',')})` }} />
                    {net.id}
                  </span>
                ))}
                <span className="dfc-net" title="Subcortex (Tian S2)">
                  <span className="dfc-swatch" style={{ background: 'rgba(255,255,255,0.28)' }} />
                  Sub
                </span>
              </span>
            )}
          </p>
          <div className="dfc-legend">
            <span className="dfc-strip" />
            <span className="dfc-ticks ba-type-caption">
              <span>{`−${h.toFixed(2)}`}</span>
              <span>0</span>
              <span>{`+${h.toFixed(2)}`}</span>
            </span>
            <span className="dfc-key ba-type-caption">
              <span className="dfc-swatch" style={{ background: `rgb(${NO_EDGE.join(',')})` }} /> Grey: no edge measured, the diagonal too · one scale for every condition
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
